import { AnyAction } from 'redux';
import {
  App,
  ConfirmationState,
  FeatureToggles,
  MultiangebotAdressenValidationResult,
  PersonenSearchListItem,
  PersonensucheModal,
  Regexps,
  Rolle,
  StandaloneProdukt,
  VnWechselVertraegeAendern,
  Vorvertrag,
  FileDownloadError,
} from '@ps20-frontend/frontend-models';
import {
  GlobalActions,
  InitializeShopPayload,
  RoleDeterminedPayload,
  Settings,
  ZaehlstueckeReceived
} from '@ps20-frontend/shop-integration-redux';
import {
  CHANGE_SEARCH_DIALOG_STATE,
  isSearchDialogReducerAction,
  SearchDialogContainerState,
  searchDialogReducer
} from '@ps20-frontend/gui-components';
import { IAppState } from '@ps20-frontend/apps/dashboard-store';
import { Kooperation, Role } from '@ps20/bff-models';
import { StandaloneDialogModel } from '../model/standaloneDialog';
import { DashboardActions } from '../dashboard.actions';
import { WarenkorbAngebot } from './produkte.reducer';
import { VertragItem } from './weitereVertraege.reducer';

const PERSONEN_LIST_COLUMNS = ['icon', 'name', 'geburtsdatum', 'ort', 'strasse', 'status'];

export interface TransientState extends Rolle {
  immoModalOpenForApp: App;
  immoModalOpenForAngebot?: string;
  unfallPersonenAuswahlOpen: boolean;
  vermittlerwechselModalOpen: boolean;
  unfallModalOpenForApp?: string;
  personensucheModal: PersonensucheModal;
  standaloneModalOpenForApp: StandaloneDialogModel;
  standaloneModalOpenForVertrag: StandaloneProdukt;
  ehvModalOpenForVertrag: Vorvertrag;
  rsZusammenfuehrenModalOpenForVertrag?: Vorvertrag;
  tarifierungIsInProgress: boolean;
  shopPricingStale?: boolean;
  forwardToUnderwriterInProgress: boolean;
  forwardToAgentInProgress: boolean;
  vorbelegungIsInProgress: boolean;
  amisCRMRquestInProgress: boolean;
  requestFailed: boolean;
  featureToggles: FeatureToggles;
  regexps: Regexps;
  ngspDeactivated?: boolean;
  isSdwSession?: boolean;
  wasUw?: boolean;
  accessToken?: string;
  showRabattHinweis: boolean;
  showVorteilsRabattHinweis: boolean;
  kraftRabattHinweisClosed: boolean;
  showGeladeneVorgaengeHinweis: boolean;
  kundenSuchdialog: SearchDialogContainerState<PersonenSearchListItem>;
  fremdVertraege: {
    vorvertraege: Array<Vorvertrag>;
    standaloneprodukte: Array<StandaloneProdukt>;
  };
  newFremdVertraegeState: Array<StandaloneProdukt | Vorvertrag>;
  weitereVertraegeCached: Array<VertragItem>;
  updatefremdvertraegeIsInProgress: boolean;
  fremdVnChanged: VnWechselVertraegeAendern;
  updateWeitereVertraegeIsInProgress: boolean;
  multiangebotVwgAdressValidationResult: MultiangebotAdressenValidationResult;
  productSelected: WarenkorbAngebot;
  settings: Settings;
  maxAnzahlVorvertraegeErreicht: boolean;
  isAuslandsImmobilie?: boolean;
  kooperationen: Kooperation[];
  customAngebotsdatum?: string;
  isAnfragepflichtigeInformationen?: boolean;
  fileModificationInProgress?: boolean;
  permasession?: boolean;
  fileDownloadErrors?: FileDownloadError[];
  showTransferToAgentPage?: boolean;
  underReview?: boolean;
  uwReviewBlocksEditing?: boolean;
}

export const transientStateReducer = function (
  sectionState: TransientState = null,
  action: AnyAction,
  state?: IAppState,
): TransientState {
  switch (action.type) {
    case DashboardActions.IMMO_MODAL_OPEN: {
      return {
        ...sectionState,
        immoModalOpenForApp: action.payload.appId,
        immoModalOpenForAngebot: action.payload.angebotsId,
        isAuslandsImmobilie: null,
        multiangebotVwgAdressValidationResult: null,
      };
    }
    case DashboardActions.IMMO_MODAL_CLOSE: {
      return { ...sectionState, immoModalOpenForApp: null };
    }
    case DashboardActions.UNF_PERS_MODAL_OPEN: {
      return {
        ...sectionState,
        unfallPersonenAuswahlOpen: true,
        unfallModalOpenForApp: action.payload.angebotsId,
      };
    }
    case DashboardActions.UNF_PERS_MODAL_CLOSE: {
      return { ...sectionState, unfallPersonenAuswahlOpen: false };
    }
    case DashboardActions.STANDALONE_PRODUKT_MODAL_OPEN: {
      return { ...sectionState, standaloneModalOpenForApp: action.payload };
    }
    case DashboardActions.STANDALONE_PRODUKT_MODAL_CLOSE: {
      return { ...sectionState, standaloneModalOpenForApp: null };
    }
    case DashboardActions.VERMITTLERWECHSEL_MODAL_OPEN: {
      return { ...sectionState, vermittlerwechselModalOpen: true };
    }
    case DashboardActions.VERMITTLERWECHSEL_MODAL_CLOSE: {
      return { ...sectionState, vermittlerwechselModalOpen: false };
    }
    case DashboardActions.STANDALONE_EINSCHLIESSEN_MODAL_OPEN: {
      return { ...sectionState, standaloneModalOpenForVertrag: action.payload };
    }
    case DashboardActions.STANDALONE_EINSCHLIESSEN_MODAL_CLOSE: {
      return { ...sectionState, standaloneModalOpenForVertrag: null };
    }
    case DashboardActions.RS_ZUSAMMENFUEHREN_MODAL_OPEN: {
      return { ...sectionState, rsZusammenfuehrenModalOpenForVertrag: action.payload };
    }
    case DashboardActions.RS_ZUSAMMENFUEHREN_MODAL_CLOSE: {
      return { ...sectionState, rsZusammenfuehrenModalOpenForVertrag: null };
    }
    case DashboardActions.EHV_MODAL_OPEN: {
      return { ...sectionState, ehvModalOpenForVertrag: action.payload };
    }
    case DashboardActions.EHV_MODAL_CLOSE: {
      return { ...sectionState, ehvModalOpenForVertrag: null };
    }
    case GlobalActions.VORVERTRAG_BEIBEHALTEN:
    case GlobalActions.TARIFIERUNG_HAS_STARTED: {
      return { ...sectionState, tarifierungIsInProgress: true };
    }
    case GlobalActions.ALL_TARIFIERUNGEN_HAVE_ENDED: {
      return { ...sectionState, tarifierungIsInProgress: false };
    }
    case GlobalActions.TARIFIERE_SHOP_STALE: {
      return { ...sectionState, shopPricingStale: true };
    }
    case GlobalActions.TARIFIERE_SHOP_SUCCESS: {
      return sectionState?.shopPricingStale === true
        ? { ...sectionState, shopPricingStale: false }
        : sectionState;
    }
    case DashboardActions.FORWARD_TO_UNDERWRITER_STARTED: {
      return { ...sectionState, forwardToUnderwriterInProgress: true };
    }
    case DashboardActions.FORWARD_TO_UNDERWRITER_FINISHED: {
      return { ...sectionState, forwardToUnderwriterInProgress: false };
    }
    case DashboardActions.FORWARD_TO_AGENT_STARTED: {
      return { ...sectionState, forwardToAgentInProgress: true };
    }
    case DashboardActions.FORWARD_TO_AGENT_FINISHED: {
      return { ...sectionState, forwardToAgentInProgress: false };
    }
    case DashboardActions.UPDATE_FREMDVERTRAEGE_STARTED: {
      return { ...sectionState, updatefremdvertraegeIsInProgress: true };
    }
    case DashboardActions.START_FREMD_VERTRAEGE_AENDERN: {
      return { ...sectionState, newFremdVertraegeState: action.payload };
    }
    case DashboardActions.WEITERE_VERTRAEGE_UBERNEHMEN_CACHE: {
      const payload = action.payload as VertragItem[];
      return { ...sectionState, weitereVertraegeCached: payload };
    }
    case DashboardActions.UPDATE_FREMDVERTRAEGE_FINISHED: {
      return { ...sectionState, updatefremdvertraegeIsInProgress: false };
    }
    case DashboardActions.CACHE_FREMD_VN_CHANGED: {
      return { ...sectionState, fremdVnChanged: action.payload };
    }
    case DashboardActions.UPDATE_WEITERE_VERTRAEGE_STARTED: {
      return { ...sectionState, updateWeitereVertraegeIsInProgress: true };
    }
    case DashboardActions.UPDATE_WEITERE_VERTRAEGE_FINISHED: {
      return { ...sectionState, updateWeitereVertraegeIsInProgress: false };
    }
    case DashboardActions.KRAFT_RABATT_HINWEIS_CLOSED: {
      return { ...sectionState, kraftRabattHinweisClosed: action.payload };
    }
    case GlobalActions.ZAEHLSTUECKE_RECEIVED: {
      const actionPayload: ZaehlstueckeReceived = action.payload;
      return {
        ...sectionState,
        kraftRabattHinweisClosed:
          state.effektiveZaehlstuecke > 2 && actionPayload.effektiveZaehlstuecke > 2
            ? sectionState.kraftRabattHinweisClosed
            : actionPayload.effektiveZaehlstuecke <= 2,
      };
    }
    case GlobalActions.FEATURE_TOGGLE_RECEIVED: {
      const actionPayload: FeatureToggles = action.payload;
      return { ...sectionState, featureToggles: actionPayload };
    }
    case GlobalActions.REGEXPS_RECEIVED: {
      const actionPayload: Regexps = action.payload;
      return { ...sectionState, regexps: actionPayload };
    }
    case GlobalActions.SET_ACCESS_TOKEN: {
      return { ...sectionState, accessToken: action.payload };
    }
    case GlobalActions.SHOP_INIT: {
      const actionPayload: InitializeShopPayload = action.payload;
      return {
        ...sectionState,
        ngspDeactivated: actionPayload.ngspDeactivated,
        isSdwSession: ![null, undefined].includes(actionPayload.sdw?.partyId),
        permasession: actionPayload.permasession,
        customAngebotsdatum: actionPayload.customAngebotsdatum,
      };
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return { ...sectionState, role: actionPayload.role, wasUw: actionPayload.role === Role.GER_SDW_UW_USER ? true : sectionState.wasUw };
    }
    case GlobalActions.PRODUKT_DELETED:
      return { ...sectionState, showRabattHinweis: false };
    case GlobalActions.RABATT_HINWEIS_CHANGED: {
      return { ...sectionState, showRabattHinweis: action.payload };
    }
    case GlobalActions.VORTEILS_RABATT_HINWEIS_CHANGED: {
      return { ...sectionState, showVorteilsRabattHinweis: action.payload };
    }
    case GlobalActions.UPDATE_VORVERTRAEGE_FAILED: {
      return { ...sectionState, showGeladeneVorgaengeHinweis: true };
    }
    case CHANGE_SEARCH_DIALOG_STATE: {
      if (isSearchDialogReducerAction<PersonenSearchListItem>(action)) {
        return {
          ...sectionState,
          kundenSuchdialog: searchDialogReducer(
            sectionState && sectionState.kundenSuchdialog,
            action,
          ),
        };
      }
      return sectionState;
    }
    case DashboardActions.UPDATE_VORVERTRAEGE_FOR_MODAL: {
      return { ...sectionState, fremdVertraege: action.payload };
    }
    case GlobalActions.UPDATE_VORVERTRAEGE: {
      return {
        ...sectionState,
        maxAnzahlVorvertraegeErreicht: action.payload.maxAnzahlVorvertraegeErreicht,
      };
    }
    case DashboardActions.SET_MULTIANGEBOT_VWG_ADRESS_VALIDATION_RESULT: {
      const actionPayload: MultiangebotAdressenValidationResult = action.payload;
      return {
        ...sectionState,
        multiangebotVwgAdressValidationResult: actionPayload,
      };
    }
    case DashboardActions.IMMO_AUSGEWAEHLT: {
      return {
        ...sectionState,
        multiangebotVwgAdressValidationResult: null,
        isAuslandsImmobilie: null,
      };
    }
    case DashboardActions.NEUE_IMMO_AUSGEWAEHLT: {
      const actionPayload = action.payload;
      return {
        ...sectionState,
        multiangebotVwgAdressValidationResult: null,
        isAuslandsImmobilie: actionPayload,
      };
    }
    case DashboardActions.FILE_MODIFICATION_IN_PROGRESS: {
      return { ...sectionState, fileModificationInProgress: true };
    }
    case DashboardActions.FILE_MODIFICATION_FINISHED: {
      return { ...sectionState, fileModificationInProgress: false };
    }
    case GlobalActions.TOGGLE_PERSONENSUCHE_MODAL: {
      let kundenSuchdialog = getInitialPersonensucheState();
      if (action.payload === PersonensucheModal.CLOSED) {
        return { ...sectionState, personensucheModal: action.payload, kundenSuchdialog };
      } else {
        kundenSuchdialog = {
          ...sectionState.kundenSuchdialog,
          items: [],
          confirmation: ConfirmationState.NOT_STARTED,
          selectedItem: null,
        };
        return { ...sectionState, personensucheModal: action.payload, kundenSuchdialog };
      }
    }
    case GlobalActions.AMIS_CRM_IMMOBILIE_REQUESTED: {
      return {
        ...sectionState,
        amisCRMRquestInProgress: true,
        requestFailed: false,
      };
    }
    case GlobalActions.AMIS_IMMOBILIEN_TRANSFORMED: {
      return { ...sectionState, amisCRMRquestInProgress: false };
    }
    case GlobalActions.AMIS_CRM_IMMOBILIE_REQUEST_FAILED: {
      return {
        ...sectionState,
        amisCRMRquestInProgress: false,
        requestFailed: true,
      };
    }
    case GlobalActions.VORBELEGUNG_REQUESTED: {
      return { ...sectionState, vorbelegungIsInProgress: true };
    }
    case GlobalActions.VORBELEGUNG_FINISHED: {
      return { ...sectionState, vorbelegungIsInProgress: false };
    }
    case GlobalActions.PRODUKT_IN_MODAL:
      return {
        ...sectionState,
        productSelected: action.produkt,
      };
    case GlobalActions.SETTINGS_RECEIVED:
      return {
        ...sectionState,
        settings: action.payload,
      };
    case GlobalActions.KOOPERATIONEN_LIST_RECEIVED: {
      return {
        ...sectionState,
        kooperationen: action.payload,
      };
    }
    case GlobalActions.SET_ANFRAGEPFLICHTIGE_INFORMATIONEN: {
      return { ...sectionState, ...action.payload };
    }
    case GlobalActions.SET_SHOW_TRANSFER_TO_AGENT_PAGE: {
      return { ...sectionState, ...action.payload };
    }
    case GlobalActions.SET_EDITING_UW_APPROVED_OFFER: {
      return { ...sectionState, ...action.payload };
    }
    case DashboardActions.ADD_FILE_DOWNLOAD_ERROR: {
      const errors = sectionState.fileDownloadErrors || [];
      const fileNameExists = errors.some(error => error.fileName === action.payload.fileName);
      if (fileNameExists) {
        return sectionState;
      }
      return { ...sectionState, fileDownloadErrors: [...errors, action.payload] };
    }
    case DashboardActions.REMOVE_FILE_DOWNLOAD_ERROR: {
      const errors = sectionState.fileDownloadErrors || [];
      return {
        ...sectionState,
        fileDownloadErrors: errors.filter(error => error.id !== action.payload),
      };
    }
    case DashboardActions.REMOVE_FILE_DOWNLOAD_ERROR_BY_FILENAME: {
      const errors = sectionState.fileDownloadErrors || [];
      return {
        ...sectionState,
        fileDownloadErrors: errors.filter(error => error.fileName !== action.payload),
      };
    }
    case DashboardActions.CLEAR_FILE_DOWNLOAD_ERRORS: {
      return { ...sectionState, fileDownloadErrors: [] };
    }
    case GlobalActions.SAVE_STATUS_RECEIVED: {
      return {
        ...sectionState,
        underReview: action.payload.underReview,
      };
    }
    case GlobalActions.SET_UW_REVIEW_BLOCKS_EDITING: {
      return {
        ...sectionState,
        uwReviewBlocksEditing: action.payload.uwReviewBlocksEditing,
      };
    }
    default:
      return sectionState;
  }
};

export function getInitialPersonensucheState(): SearchDialogContainerState<PersonenSearchListItem> {
  return {
    searchString: '',
    items: [],
    open: false,
    error: false,
    errorMessage: '',
    loading: false,
    selectedItem: null,
    confirmed: false,
    confirmation: ConfirmationState.NOT_STARTED,
    renderedColumns: PERSONEN_LIST_COLUMNS,
    personensucheModal: PersonensucheModal.CLOSED,
  };
}
